import { platformBrowserDynamic } from '@angular/platform-browser-dynamic';
import * as Sentry from '@sentry/angular';
import { fullStoryIntegration } from '@sentry/fullstory';
import { PlUtil } from '@app/common/pl-util';
import * as FullStory from '@fullstory/browser';
import { CDN_URL } from '@common/constant/cdn';
import { AppModule } from './app/app.module';

const fullStoryKey = PlUtil.getEnvVar('fullstory_key', 'pl-fullstory-key');
if (fullStoryKey) {
  FullStory.init({ orgId: fullStoryKey });
}
const sentryKey = PlUtil.getEnvVar('sentry_dsn', 'pl-sentry-key');
if (sentryKey) {
  Sentry.init({
    dsn: sentryKey,
    integrations: [
      fullStoryIntegration('presencelearning', { client: FullStory }),
    ],
  });
}

// Load CDN styles globally before Angular bootstraps
const cdnStylesheets = [
  '/css/common/colors.css',
  '/css/common/typography.css',
  '/css/common/utils.css',
];

function loadCDNStylesheet(url: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = url;

    let settled = false;

    // TODO: Remove this timeout once CDN is production-ready and proven stable
    // Safety timeout to prevent infinite loading if CDN hangs without responding
    // This ensures the app bootstraps even if the CDN accepts connections but never responds
    const timeoutId = setTimeout(() => {
      if (!settled) {
        settled = true;
        // eslint-disable-next-line no-console
        console.warn(`CDN stylesheet load timeout, proceeding anyway: ${url}`);
        resolve(); // Resolve (not reject) to allow app to bootstrap with degraded styling
      }
    }, 5000);

    link.onload = () => {
      if (!settled) {
        settled = true;
        clearTimeout(timeoutId);
        resolve();
      }
    };

    link.onerror = () => {
      if (!settled) {
        settled = true;
        clearTimeout(timeoutId);
        const error = new Error(`Failed to load CDN stylesheet: ${url}`);
        // eslint-disable-next-line no-console
        console.error(error.message);
        reject(error);
      }
    };

    document.head.appendChild(link);
  });
}

Promise.allSettled(
  cdnStylesheets.map((stylesheet) =>
    loadCDNStylesheet(`${CDN_URL}${stylesheet}`)
  )
)
  .then((results) => {
    const failures = results.filter((r) => r.status === 'rejected');
    if (failures.length > 0) {
      // eslint-disable-next-line no-console
      console.warn(
        `${failures.length} CDN stylesheet(s) failed to load. App may display incorrectly.`
      );
    }
    return platformBrowserDynamic().bootstrapModule(AppModule);
  })
  .catch((err) => {
    // eslint-disable-next-line no-console
    console.error(err);
  });
